/**
 * useEditorNavigation — Section click, back, breadcrumb, and nav handlers.
 * Extracted from FinancialModelEditor.tsx lines ~1970–2039 (Phase 3A).
 */

import { useCallback, useEffect } from 'react';
import { ModelSection } from '../../../types/ModelSection';

interface UseEditorNavigationParams {
    viewMode: 'overview' | 'section';
    currentSection: ModelSection | null;
    sections: ModelSection[];
    setCurrentSection: (section: ModelSection | null) => void;
    setViewMode: (mode: 'overview' | 'section') => void;
    setModelDataSelected: (item: any) => void;
    setSearchStrings: (val: { Input: string; Output: string }) => void;
    loadSectionData: (sectionId: string, forceReload?: boolean) => void;
}

export function useEditorNavigation({
    viewMode,
    currentSection,
    sections,
    setCurrentSection,
    setViewMode,
    setModelDataSelected,
    setSearchStrings,
    loadSectionData,
}: UseEditorNavigationParams) {
    const handleSectionClick = useCallback((section: ModelSection) => {
        setCurrentSection(section);
        setViewMode('section');
        setModelDataSelected(null);
        loadSectionData(section.config.id);
    }, [setCurrentSection, setViewMode, setModelDataSelected, loadSectionData]);

    const handleBackToOverview = useCallback(() => {
        setCurrentSection(null);
        setViewMode('overview');
        setModelDataSelected(null);
        setSearchStrings({ 'Input': '', 'Output': '' });
    }, [setCurrentSection, setViewMode, setModelDataSelected, setSearchStrings]);

    const handleBreadcrumbClick = useCallback((index: number) => {
        if (index === 0 && viewMode === 'section') {
            handleBackToOverview();
        }
    }, [viewMode, handleBackToOverview]);

    const handleSectionNavClick = useCallback((section: ModelSection) => {
        if (section.config.id !== currentSection?.config.id) {
            setCurrentSection(section);
            setModelDataSelected(null);
            setSearchStrings({ 'Input': '', 'Output': '' });
            loadSectionData(section.config.id);
        }
    }, [currentSection, setCurrentSection, setModelDataSelected, setSearchStrings, loadSectionData]);

    // Keyboard shortcuts for navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && viewMode === 'section') {
                e.preventDefault();
                handleBackToOverview();
            }

            if (e.altKey && e.key === '2') {
                e.preventDefault();
                setViewMode('overview');
            }

        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [viewMode, handleBackToOverview, setViewMode]);

    return {
        handleSectionClick,
        handleBackToOverview,
        handleBreadcrumbClick,
        handleSectionNavClick,
    };
}
